import React from "react";
import { Box, Typography, Tooltip, Chip } from "@mui/material";
import { useTheme, alpha } from "@mui/material/styles";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import ForumOutlinedIcon from "@mui/icons-material/ForumOutlined";
import TaskAltRounded from "@mui/icons-material/TaskAltRounded";
import PendingRounded from "@mui/icons-material/PendingRounded";
import ArrowOutwardOutlinedIcon from "@mui/icons-material/ArrowOutwardOutlined";
import { Button } from "components";
import { useNavigate } from "react-router-dom";

function formatDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function timeAgo(dateStr, t) {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now - date;
  const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
  if (diffDays === 0) return t("dashboard.timeLabels.today");
  if (diffDays === 1) return t("dashboard.timeLabels.yesterday");
  if (diffDays < 7)
    return t("dashboard.timeLabels.daysAgo", { count: diffDays });
  const weeks = Math.floor(diffDays / 7);
  if (diffDays < 31)
    return weeks === 1
      ? t("dashboard.timeLabels.weekAgo", { count: weeks })
      : t("dashboard.timeLabels.weeksAgo", { count: weeks });
  return formatDate(dateStr);
}

export default function InfoRequestCard({
  id,
  topic_name,
  topic_description,
  contributor_count,
  createdAt = new Date().toISOString(),
  is_current_user_contribute = false,
  status = "active", // Add status prop with default value
}) {
  const navigate = useNavigate();
  const theme = useTheme();
  const { t } = useTranslation();
  const isLight = theme.palette.mode === "light";

  // Get current user from Redux state
  const currentUser = useSelector((state) => state.auth.user);

  // Check if current user is admin
  const isAdmin = currentUser?.roles?.some(
    (role) => role.toLowerCase() === "admin",
  );

  // Check if topic is inactive
  const isInactive = status?.toLowerCase() === "inactive";

  // Keep your previous card styles as-is
  const cardBg = isLight ? "#f4f8fb" : "#233044";
  const borderColor = isLight ? "rgba(2,89,165,0.10)" : "rgba(0,188,212,0.13)";
  const boxShadow = isLight
    ? "0 2px 10px 0 rgba(2,89,165,0.07)"
    : "0 3px 12px 0 rgba(0,188,212,0.10)";

  // Theme-driven chip styling (success when contributed, warning otherwise)
  const statusColor = is_current_user_contribute
    ? theme.palette.success.main
    : theme.palette.warning.main;
  const statusBg = alpha(statusColor, isLight ? 0.12 : 0.18);
  const statusBorder = alpha(statusColor, 0.35);
  const statusLabel = is_current_user_contribute
    ? t("dashboard.statusLabels.contributed")
    : t("dashboard.statusLabels.openToContribute");
  const StatusIcon = is_current_user_contribute
    ? TaskAltRounded
    : PendingRounded;

  return (
    <Box
      onClick={() => navigate(`/single_topic/${id}`)}
      sx={{
        px: { xs: 1.8, sm: 2, md: 2.2 },
        pt: { xs: 3.5, md: 4 },
        pb: { xs: 1.8, md: 2 },
        borderRadius: { xs: 2.5, md: 3 },
        border: `1.2px solid ${borderColor}`,
        background: cardBg,
        boxShadow,
        height: { xs: 165, sm: 175, md: 180 },
        display: "flex",
        flexDirection: "column",
        transition: "box-shadow 0.18s, transform 0.15s",
        position: "relative",
        cursor: "pointer",
        // Apply visual disabled effect when inactive
        opacity: isInactive ? 0.6 : 1,
        filter: isInactive ? "grayscale(0.3)" : "none",
        "&:hover": {
          transform: "translateY(-2px)",
          boxShadow: isLight
            ? "0 4px 16px 0 rgba(2,89,165,0.12)"
            : "0 6px 20px 0 rgba(0,188,212,0.16)",
        },
      }}
    >
      {/* Status chip at the top inside the card - Hide when topic is inactive */}
      {!isInactive && (
        <Box sx={{ position: "absolute", top: "-2px", left: 15, zIndex: 1 }}>
          <Chip
            size="small"
            label={statusLabel}
            icon={<StatusIcon sx={{ fontSize: { xs: 12, md: 14 } }} />}
            variant="outlined"
            sx={{
              height: { xs: 20, md: 22 },
              borderTopLeftRadius: 0,
              borderTopRightRadius: 0,
              borderBottomRightRadius: { xs: 10, md: 12 },
              borderBottomLeftRadius: { xs: 10, md: 12 },
              px: 0.5,
              py: 1.5,
              fontSize: { xs: "0.7rem", md: "0.75rem" },
              bgcolor: statusBg,
              color: statusColor,
              border: `2px solid ${statusBorder}`,
              "& .MuiChip-icon": { color: statusColor, ml: 0.25 },
              "& .MuiChip-label": { px: { xs: 0.5, md: 0.75 } },
            }}
          />
        </Box>
      )}

      {/* Header row: Title left, Date right */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 0.5,
          gap: 1,
          minWidth: 0,
        }}
      >
        <Typography
          variant="subtitle1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: "0.9rem", sm: "0.95rem", md: "1rem" },
            color: theme.palette.text.primary,
            maxWidth: "68%",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
          title={topic_name}
        >
          {topic_name}
        </Typography>

        <Tooltip title={formatDate(createdAt)}>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{
              opacity: 0.82,
              fontWeight: 500,
              fontSize: { xs: "0.65rem", md: "0.75rem" },
              letterSpacing: 0.2,
              fontStyle: "italic",
              textAlign: "right",
              minWidth: { xs: 56, md: 68 },
              flexShrink: 0,
            }}
          >
            {timeAgo(createdAt, t)}
          </Typography>
        </Tooltip>
      </Box>

      <Typography
        variant="body2"
        sx={{
          color: theme.palette.text.secondary,
          fontSize: { xs: "0.8rem", md: "0.875rem" },
          mb: 1,
          display: "-webkit-box",
          WebkitLineClamp: 2,
          WebkitBoxOrient: "vertical",
          overflow: "hidden",
          textOverflow: "ellipsis",
          minHeight: { xs: 32, md: 36 },
        }}
      >
        {topic_description}
      </Typography>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: !isAdmin ? "flex-end" : "space-between",
          gap: 1.1,
          mt: 2,
        }}
      >
        {/*  contributor count for admin users */}
        {isAdmin && (
          <Box sx={{ display: "flex" }}>
            <Box
              sx={{
                background: isLight ? "#eaf3fb" : "#181f2a",
                borderRadius: 1.5,
                width: { xs: 24, md: 28 },
                height: { xs: 24, md: 28 },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mr: 0.5,
              }}
            >
              <ForumOutlinedIcon
                sx={{
                  fontSize: { xs: 16, md: 18 },
                  color: theme.palette.primary.main,
                }}
              />
            </Box>
            <Typography
              variant="caption"
              sx={{
                color: theme.palette.text.secondary,
                fontWeight: 500,
                fontSize: { xs: "0.8rem", md: "0.875rem" },
              }}
            >
              {contributor_count} {t("dashboard.contributionsCount")}
            </Typography>
          </Box>
        )}

        <Button
          variant="outlined"
          endIcon={
            <ArrowOutwardOutlinedIcon sx={{ fontSize: { xs: 14, md: 16 } }} />
          }
          onClick={(e) => {
            e.stopPropagation();
            navigate(`/single_topic/${id}`);
          }}
          sx={{
            width: isAdmin ? "auto" : "100%",
            borderRadius: 2,
            textTransform: "none",
            fontWeight: 500,
            fontSize: { xs: "0.75rem", md: "0.875rem" },
            px: { xs: 1.2, md: 1.5 },
            py: { xs: 0.4, md: 0.5 },
            color: theme.palette.primary.main,
            borderColor: alpha(theme.palette.primary.main, 0.35),
            background: alpha(theme.palette.primary.main, 0.06),
            "&:hover": {
              borderColor: theme.palette.primary.main,
              background: alpha(theme.palette.primary.main, 0.1),
            },
          }}
        >
          {t("dashboard.viewButton")}
        </Button>
      </Box>
    </Box>
  );
}
